import React, { useEffect, useState } from "react";
import axios from "../../api/axios";

// MUI components
import {
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Icon from "@mui/material/Icon";

// Custom components
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

function NonRegularEarnings() {
  const [earnings, setEarnings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [filterPfNo, setFilterPfNo] = useState("");

  const [currentItem, setCurrentItem] = useState({
    id: null,
    pf_no: "",
    staff_category_id: "",
    title: "",
    description: "",
    amount: "",
    month: "",
    year: new Date().getFullYear(),
    taxable: false,
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch all non-regular earnings
  const fetchEarnings = async () => {
    try {
      let url = "/non-regular-earnings";
      if (filterPfNo) {
        url = `/non-regular-earnings/pf/${filterPfNo}`;
      }
      const response = await axios.get(url);
      setEarnings(response.data.data || response.data); // handle paginated/non-paginated
    } catch (err) {
      console.error("Failed to fetch non-regular earnings", err);
    }
  };

  // Fetch staff categories for dropdown
  const fetchCategories = async () => {
    try {
      const response = await axios.get("/staff-categories");
      setCategories(response.data);
    } catch (err) {
      console.error("Failed to fetch staff categories", err);
    }
  };

  useEffect(() => {
    fetchEarnings();
    fetchCategories();
  }, []);

  // Save or update record
  const handleSave = async () => {
    try {
      const payload = {
        pf_no: currentItem.pf_no,
        staff_category_id: currentItem.staff_category_id || null,
        title: currentItem.title,
        description: currentItem.description,
        amount: currentItem.amount,
        month: currentItem.month,
        year: currentItem.year,
        taxable: currentItem.taxable,
      };

      if (currentItem.id) {
        await axios.put(`/non-regular-earnings/${currentItem.id}`, payload);
        setSnackbar({ open: true, message: "Earning updated successfully" });
      } else {
        await axios.post("/non-regular-earnings", payload);
        setSnackbar({ open: true, message: "Earning added successfully" });
      }

      fetchEarnings();
      setOpenDialog(false);
      setCurrentItem({
        id: null,
        pf_no: "",
        staff_category_id: "",
        title: "",
        description: "",
        amount: "",
        month: "",
        year: new Date().getFullYear(),
        taxable: false,
      });
    } catch (err) {
      console.error(err);
      alert("Something went wrong while saving.");
    }
  };

  // Edit record
  const handleEdit = (item) => {
    setCurrentItem(item);
    setOpenDialog(true);
  };

  // Delete record
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this earning?")) {
      try {
        await axios.delete(`/non-regular-earnings/${id}`);
        fetchEarnings();
        setSnackbar({ open: true, message: "Earning deleted successfully" });
      } catch (err) {
        console.error("Delete failed", err);
      }
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedItems = earnings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <Grid container justifyContent="space-between" alignItems="center" px={2} mb={2}>
          <Typography variant="h5">Non-Regular Earnings</Typography>
          <Grid item display="flex" gap={2}>
            <TextField
              label="Filter by PF No"
              size="small"
              value={filterPfNo}
              onChange={(e) => setFilterPfNo(e.target.value)}
            />
            <Button variant="outlined" onClick={fetchEarnings}>
              Filter
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
              sx={{ color: "#fff" }}
            >
              Add New
            </Button>
          </Grid>
        </Grid>

        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
          <Table>
            <TableBody>
              {paginatedItems.length > 0 ? (
                paginatedItems.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{item.pf_no || "-"}</TableCell>
                    <TableCell>
                      {categories.find((c) => c.id === item.staff_category_id)?.name ||
                        item.staff_category_id ||
                        "-"}
                    </TableCell>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>{item.amount}</TableCell>
                    <TableCell>{`${item.month}/${item.year}`}</TableCell>
                    <TableCell>{item.taxable ? "Yes" : "No"}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleEdit(item)}>
                        <Icon>edit</Icon>
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(item.id)}>
                        <Icon>delete</Icon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ padding: 3 }}>
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={earnings.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      </MDBox>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>{currentItem.id ? "Edit Earning" : "Add New Earning"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="PF No"
            margin="normal"
            value={currentItem.pf_no}
            onChange={(e) => setCurrentItem({ ...currentItem, pf_no: e.target.value })}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Staff Category</InputLabel>
            <Select
              value={currentItem.staff_category_id}
              label="Staff Category"
              onChange={(e) =>
                setCurrentItem({ ...currentItem, staff_category_id: e.target.value })
              }
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Title"
            margin="normal"
            value={currentItem.title}
            onChange={(e) => setCurrentItem({ ...currentItem, title: e.target.value })}
          />

          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={2}
            value={currentItem.description}
            onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
          />

          <TextField
            fullWidth
            label="Amount"
            margin="normal"
            type="number"
            value={currentItem.amount}
            onChange={(e) => setCurrentItem({ ...currentItem, amount: e.target.value })}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Month"
                type="number"
                margin="normal"
                value={currentItem.month}
                onChange={(e) => setCurrentItem({ ...currentItem, month: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                margin="normal"
                value={currentItem.year}
                onChange={(e) => setCurrentItem({ ...currentItem, year: e.target.value })}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Checkbox
                checked={currentItem.taxable}
                onChange={(e) => setCurrentItem({ ...currentItem, taxable: e.target.checked })}
              />
            }
            label="Taxable"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default NonRegularEarnings;
